import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { articles, categories } from '@/mocks/data';
import { useJsonLd } from '@/hooks/useJsonLd';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === Number(id));
  const category = article ? categories.find((c) => c.slug === article.category) : null;

  const relatedArticles = article
    ? articles.filter((a) => a.category === article.category && a.id !== article.id).slice(0, 3)
    : [];

  const otherFeatured = article
    ? articles.filter((a) => a.featured && a.id !== article.id).slice(0, 3)
    : [];

  const articleUrl = article ? `https://z3ratu1.cn/article/${article.id}` : '';

  useJsonLd(
    article ? {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Article',
          '@id': articleUrl,
          headline: article.title,
          description: article.excerpt,
          image: article.coverImage,
          author: {
            '@type': 'Person',
            name: article.author,
          },
          datePublished: article.publishDate,
          inLanguage: 'zh-CN',
          isPartOf: { '@id': 'https://z3ratu1.cn/#website' },
          publisher: { '@id': 'https://z3ratu1.cn/#organization' },
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: '首页',
              item: 'https://z3ratu1.cn/',
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: category?.name || '分类',
              item: `https://z3ratu1.cn/category/${article.category}`,
            },
            {
              '@type': 'ListItem',
              position: 3,
              name: article.title,
              item: articleUrl,
            },
          ],
        },
      ],
    } : { '@context': 'https://schema.org', '@graph': [] }
  );

  useEffect(() => {
    if (article) {
      document.title = `${article.title} - 亚洲一级日本精选内容导航平台`;
    }
  }, [article]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-24 pb-14">
          <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-4">文章未找到</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700 text-sm">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const fullContent = `亚洲一级日本精选内容导航平台为您带来深度解析。

${article.excerpt}

作为亚洲一级日本内容领域的权威平台，我们持续关注日本文化与亚洲融合的最新动态。本文将从多个维度深入分析${article.tags.slice(0, 2).join('和')}的核心要点，为读者提供全面而深入的内容解读。

在亚洲一级日本内容生态中，${article.title}无疑是一个值得关注的重要话题。我们通过对行业专家的深度访谈和大量一手资料的整理，力求为读者呈现最真实、最全面的信息。

首先，从历史发展的角度来看，相关领域经历了从萌芽到繁荣的演变过程。早期的探索为今天的成就奠定了坚实基础，而近年来的创新更推动了整个行业的快速发展。作为亚洲顶级内容平台，我们见证了无数精彩的时刻。

其次，在当下的环境中，技术创新和文化融合正在重塑整个行业格局。日本作为亚洲文化的重要输出国，其影响力已经远远超越了国界。亚洲一级日本频道将持续关注这些变化，为用户提供最及时的内容更新。

此外我们注意到，用户对于高质量内容的需求日益增长。我们的专业分级筛选机制正是为了满足这一需求而设计的——通过严格的审核流程和精准的算法推荐，确保每一位用户都能获得最适合自己的优质内容。

展望未来，亚洲一级日本内容导航平台将继续深耕内容质量，拓展更多领域，为全球中文用户提供更加丰富、多元的日本文化体验。我们相信，在各方的共同努力下，亚洲一级日本内容生态将迎来更加繁荣的明天。

——本文由${article.author}撰写，首发于亚洲一级日本精选内容导航平台。未经授权，禁止转载。`;

  return (
    <>
      <Navbar />
      <main className="min-h-screen pt-24 pb-14">
        <article className="max-w-4xl mx-auto px-4 md:px-6">
          <header className="mb-6">
            <Breadcrumb items={[
              { label: category?.name || '分类', href: category ? `/category/${category.slug}` : undefined },
              { label: article.title },
            ]} />
          </header>

          <div className="flex flex-wrap items-center gap-2 mb-4">
            {article.tags.map((tag) => (
              <span key={tag} className="px-3 py-1 rounded-full bg-background-100 text-xs font-medium text-foreground-600 whitespace-nowrap">
                {tag}
              </span>
            ))}
          </div>

          <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 leading-tight mb-4">
            {article.title}
          </h1>

          <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-8">
            <span className="flex items-center gap-1.5">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-user-line text-xs"></i>
              </div>
              {article.author}
            </span>
            <span className="flex items-center gap-1.5">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-calendar-line text-xs"></i>
              </div>
              {article.publishDate}
            </span>
            <span className="flex items-center gap-1.5">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-time-line text-xs"></i>
              </div>
              {article.readTime} 分钟阅读
            </span>
          </div>

          <div className="mb-10 rounded-lg overflow-hidden">
            <img
              src={article.coverImage}
              alt={article.title}
              title={`${article.title} - 亚洲一级日本精选内容导航平台`}
              className="w-full h-auto max-h-[500px] object-cover object-top"
            />
          </div>

          <div className="prose-custom">
            {fullContent.split('\n\n').map((paragraph, idx) => {
              if (paragraph.trim().startsWith('首先') || paragraph.trim().startsWith('其次') || paragraph.trim().startsWith('此外') || paragraph.trim().startsWith('展望')) {
                return (
                  <section key={idx}>
                    <h2 className="font-heading text-lg md:text-xl font-semibold text-foreground-900 mt-8 mb-3">
                      {paragraph.trim().split('，')[0]}
                    </h2>
                    <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                      {paragraph.trim()}
                    </p>
                  </section>
                );
              }
              if (idx === 0) {
                return (
                  <p key={idx} className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                    {paragraph.trim()}
                  </p>
                );
              }
              return (
                <p key={idx} className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  {paragraph.trim()}
                </p>
              );
            })}
          </div>

          <div className="flex items-center justify-between py-6 border-t border-b border-background-200/70 mb-10">
            <div className="flex items-center gap-2 text-sm text-foreground-500">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-share-forward-line"></i>
              </div>
              <span>分享文章</span>
            </div>
            <div className="flex items-center gap-2">
              <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 hover:bg-primary-100 text-foreground-600 hover:text-primary-600 transition-colors duration-200 cursor-pointer" aria-label="分享到微博">
                <i className="ri-weibo-line text-sm"></i>
              </button>
              <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 hover:bg-primary-100 text-foreground-600 hover:text-primary-600 transition-colors duration-200 cursor-pointer" aria-label="分享到微信">
                <i className="ri-wechat-line text-sm"></i>
              </button>
              <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 hover:bg-primary-100 text-foreground-600 hover:text-primary-600 transition-colors duration-200 cursor-pointer" aria-label="复制链接">
                <i className="ri-link text-sm"></i>
              </button>
            </div>
          </div>

          {/* Related articles */}
          {relatedArticles.length > 0 && (
            <section className="mb-14">
              <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-5">
                相关推荐
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {relatedArticles.map((rel) => (
                  <Link
                    key={rel.id}
                    to={`/article/${rel.id}`}
                    className="group block rounded-lg bg-background-50 border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer"
                  >
                    <div className="h-36 overflow-hidden">
                      <img
                        src={rel.coverImage}
                        alt={rel.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-3">
                      <h4 className="font-heading font-semibold text-xs text-foreground-900 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors duration-200">
                        {rel.title}
                      </h4>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {/* More featured */}
          {otherFeatured.length > 0 && (
            <section className="mb-14">
              <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-5">
                精选内容
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {otherFeatured.map((feat) => (
                  <Link
                    key={feat.id}
                    to={`/article/${feat.id}`}
                    className="group block rounded-lg bg-background-50 border border-background-200/70 overflow-hidden hover:border-accent-200 transition-all duration-300 cursor-pointer"
                  >
                    <div className="h-36 overflow-hidden">
                      <img
                        src={feat.coverImage}
                        alt={feat.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-3">
                      <div className="mb-1.5">
                        <span className="px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 text-xs font-medium">
                          精选
                        </span>
                      </div>
                      <h4 className="font-heading font-semibold text-xs text-foreground-900 line-clamp-2 leading-snug group-hover:text-accent-600 transition-colors duration-200">
                        {feat.title}
                      </h4>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </article>
      </main>
      <Footer />
    </>
  );
}